import { invoke } from '@shared/easyfy.decorators';
import { globalEvent } from '@shared/easyfy.core';
import Kevel, { KevelAdPlacementSlot } from '@clientWebModules/kevel';
import { HayppFeatureNames } from '@clientShared/HayppFeatureNames';
import { Features } from '@webServices/features.service';
import { logCustomMessage } from '@shared/easyfy.logger';
import EasyfyModules from '@shared/easyfy.events/easyfy.eventhandler';

@invoke
export default class KevelFloatingAd {
  private kevelFloatingAd: HTMLElement | null = document.querySelector('[data-container="kevel-floating-ad"]');
  private kevelFloatingAdLink: HTMLElement | null = this.kevelFloatingAd?.querySelector('[data-kevel-floating-ad-link]') ?? null;
  private isVisible = false;
  private isFetching = false;
  private lastScrollY = 0;
  private static readonly STORAGE_KEY = 'kevel-floating-ad-closed';

  onInit() {
    if (!this.kevelFloatingAd) return;
    if (this.hasBeenClosedThisSession()) return;
    if (!Features.FeatureIsActive(HayppFeatureNames.Kevel)) return;

    this.lastScrollY = window.scrollY;
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    globalEvent({
      type: 'click',
      events: [
        {
          event: 'close-kevel-floating-ad',
          callback: this.closeKevelFloatingAd,
        },
      ],
    });
  }

  private handleScroll = () => {
    if (this.isVisible || this.isFetching) return;

    const scrollY = window.scrollY;
    const isScrollingDown = scrollY > this.lastScrollY;
    this.lastScrollY = scrollY;

    if (isScrollingDown) {
      void this.fetchAndShowKevelFloatingAd();
    }
  };

  private fetchAndShowKevelFloatingAd = async () => {
    this.isFetching = true;
    window.removeEventListener('scroll', this.handleScroll);

    if (!this.kevelFloatingAd || !this.kevelFloatingAdLink) return;

    try {
      const floatingAdPlacement = await Kevel.Instance.fetchKevelAdPlacement(this.kevelFloatingAdLink, KevelAdPlacementSlot.FLOATING_AD_SLOT);
      if (floatingAdPlacement) {
        Kevel.Instance.applyKevelAdData(this.kevelFloatingAdLink, floatingAdPlacement);
        this.showKevelFloatingAd();
      }
    } catch (error) {
      logCustomMessage('Fetch kevel floating ad failed', {
        action: 'fetchAndShowKevelFloatingAd',
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? (error.stack ?? '') : '',
      });
    } finally {
      this.isFetching = false;
    }
  };

  private showKevelFloatingAd = () => {
    if (!this.kevelFloatingAd || !this.kevelFloatingAdLink) return;

    this.isVisible = true;
    this.kevelFloatingAd.classList.add('is-visible');
    void EasyfyModules.EasyfyEventHandler.TriggerElementImpression(this.kevelFloatingAdLink);
  };

  private closeKevelFloatingAd = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    const kevelFloatingAd = target.closest('[data-container="kevel-floating-ad"]');
    if (!kevelFloatingAd) return;

    kevelFloatingAd.classList.remove('is-visible');
    kevelFloatingAd.classList.add('is-dismissed');
    this.rememberClose();
  };

  private hasBeenClosedThisSession(): boolean {
    return sessionStorage.getItem(KevelFloatingAd.STORAGE_KEY) === 'true';
  }

  private rememberClose(): void {
    sessionStorage.setItem(KevelFloatingAd.STORAGE_KEY, 'true');
  }
}
